/**
 * Dope-sheet keyframe editing (SPEC "Timeline / Dope Sheet"). Selection lives in the store so
 * the dope sheet, the inspector and keyboard shortcuts all act on the same set of keys.
 */

import {
  clampScaleFactor,
  copyKeyframes,
  deleteKeyframes,
  getKeyframeAt,
  isAppError,
  isCanonicalBoneId,
  moveKeyframes,
  pasteKeyframes,
  reverseKeyframes,
  scaleKeyframes,
  setEasing,
  setKeyframe,
} from '../../shared';
import type {
  CanonicalAnimationClip,
  CanonicalBoneId,
  KeyframeSelection,
  QuaternionTuple,
  TweenPreset,
} from '../../shared';
import {
  commitClipEdit,
  dedupeSelection,
  keySelectionKey,
  reportError,
  reposeLiveForClipEdit,
  showToast,
  type ActionContext,
} from './context';

export interface KeyActions {
  selectKeys(selection: KeyframeSelection, options?: { additive?: boolean }): void;
  clearKeySelection(): void;
  deleteSelectedKeys(): void;
  moveSelectedKeys(deltaFrames: number): void;
  /** Mirror the selection in time across its own span (needs at least 2 selected keys). */
  reverseSelectedKeys(): void;
  /** Time-scale the selection around its earliest key (needs at least 2 selected keys). */
  scaleSelectedKeys(factor: number): void;
  beginKeyDrag(): void;
  endKeyDrag(): void;
  setEasingForSelection(preset: TweenPreset): void;
  copyKeys(): void;
  pasteKeys(atFrame?: number): void;
  /**
   * Insert one key. `options.hipsY` is the hips height the key should carry (Hips only, ignored
   * elsewhere); omitting it keeps whatever the replaced key held — `setKeyframe`'s documented
   * default — which is what a caller that knows nothing about the channel wants.
   */
  insertKeyAt(
    bone: CanonicalBoneId,
    frame: number,
    rotation: QuaternionTuple,
    options?: { hipsY?: number },
  ): void;
  deleteKey(bone: CanonicalBoneId, frame: number): void;
}

const KEY_DRAG_COALESCE_KEY = 'keys:move';

/**
 * Total keyframes in a clip. A move never CREATES a key, so `before - after` over a whole drag is
 * exactly the number of keys the gesture destroyed — which is what the user has to be told, not
 * the per-step collision count (see `endKeyDrag`).
 */
function keyCount(clip: CanonicalAnimationClip | null): number {
  if (!clip) return 0;
  let total = 0;
  for (const bone of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    total += clip.tracks[bone]?.keyframes.length ?? 0;
  }
  return total;
}

function reportDroppedKeys(ctx: ActionContext, dropped: number): void {
  showToast(
    ctx,
    'info',
    `${dropped} keyframe${dropped === 1 ? '' : 's'} dropped by the move.`,
  );
}

/**
 * The selection refs that really point at a key, with integer frames.
 *
 * `reverseKeyframes` / `scaleKeyframes` derive their span from the keys they can see, so the
 * new selection has to be rebuilt from the same set — a stale ref (a key deleted from under the
 * selection) must not shift the frames the selection lands on.
 */
function liveRefs(clip: CanonicalAnimationClip, selection: KeyframeSelection): KeyframeSelection {
  const out: KeyframeSelection = [];
  for (const ref of selection) {
    const frame = Math.max(0, Math.round(ref.frame));
    if (getKeyframeAt(clip, ref.bone, frame)) out.push({ bone: ref.bone, frame });
  }
  return out;
}

export function createKeyActions(ctx: ActionContext): KeyActions {
  /**
   * The clip as it stood when the pointer went down, and whether a drag is open at all.
   *
   * `moveSelectedKeys` runs ONCE PER POINTER STEP under `KEY_DRAG_COALESCE_KEY`, and `ui.toast`
   * is a single slot: copying `scaleSelectedKeys`' per-call toast here would make one drag past
   * frame 0 and back shout "1 keyframe dropped" ten times over, describing collisions the
   * finished gesture may not even have kept. So the loss is measured once, at the end, against
   * this baseline — the message then describes what actually happened to the clip.
   */
  let dragBaseline: CanonicalAnimationClip | null = null;
  let dragging = false;

  return {
    selectKeys(selection, options): void {
      const state = ctx.getState();
      const incoming = selection.map((ref) => ({
        bone: ref.bone,
        frame: Math.max(0, Math.round(ref.frame)),
      }));
      const next = options?.additive
        ? dedupeSelection([...state.keySelection, ...incoming])
        : dedupeSelection(incoming);

      if (
        next.length === state.keySelection.length &&
        next.every(
          (ref, i) =>
            keySelectionKey(ref.bone, ref.frame) ===
            keySelectionKey(state.keySelection[i].bone, state.keySelection[i].frame),
        )
      ) {
        return;
      }
      ctx.set({ keySelection: next });
    },

    clearKeySelection(): void {
      if (ctx.getState().keySelection.length === 0) return;
      ctx.set({ keySelection: [] });
    },

    deleteSelectedKeys(): void {
      const state = ctx.getState();
      if (!state.clip || state.keySelection.length === 0) return;
      const next = deleteKeyframes(state.clip, state.keySelection);
      commitClipEdit(ctx, 'Delete keyframes', next, []);
    },

    moveSelectedKeys(deltaFrames): void {
      const state = ctx.getState();
      if (!state.clip || state.keySelection.length === 0) return;
      // Frames are integers in serialized data, so a drag always resolves to whole frames.
      const delta = Math.round(deltaFrames);
      if (delta === 0) return;

      const result = moveKeyframes(state.clip, state.keySelection, delta);
      const selection = dedupeSelection(
        state.keySelection.map((ref) => ({
          bone: ref.bone,
          frame: Math.max(0, Math.round(ref.frame) + delta),
        })),
      );
      commitClipEdit(ctx, 'Move keyframes', result.clip, selection, KEY_DRAG_COALESCE_KEY);

      // Outside a drag (a keyboard nudge, a scripted call) this single call IS the whole gesture,
      // so it reports its own loss. Inside one, `endKeyDrag` does it — once.
      if (!dragging && result.droppedKeys > 0) reportDroppedKeys(ctx, result.droppedKeys);
    },

    reverseSelectedKeys(): void {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip || state.keySelection.length < 2) return;

      const refs = liveRefs(clip, state.keySelection);
      if (refs.length < 2) return;
      let min = refs[0].frame;
      let max = refs[0].frame;
      for (const ref of refs) {
        if (ref.frame < min) min = ref.frame;
        if (ref.frame > max) max = ref.frame;
      }
      if (min === max) return;

      const result = reverseKeyframes(clip, state.keySelection);
      const selection = dedupeSelection(
        refs.map((ref) => ({ bone: ref.bone, frame: min + max - ref.frame })),
      );
      // The playhead does not move, so nothing else refreshes the viewport — but un-keyed work
      // must survive, and the new pose has to ride the SAME undo entry as the clip.
      const live = reposeLiveForClipEdit(clip, result.clip, state, state.playhead.frame);
      if (
        !commitClipEdit(ctx, 'Reverse keys', result.clip, {
          nextSelection: selection,
          nextLivePose: live.livePose,
          nextLiveHipsY: live.liveHipsY,
        })
      ) {
        return;
      }

      if (result.overwrittenKeys > 0) {
        showToast(
          ctx,
          'info',
          `${result.overwrittenKeys} keyframe${result.overwrittenKeys === 1 ? '' : 's'} overwritten by the reverse.`,
        );
      }
    },

    scaleSelectedKeys(factor): void {
      const state = ctx.getState();
      const clip = state.clip;
      if (!clip || state.keySelection.length < 2) return;

      const refs = liveRefs(clip, state.keySelection);
      if (refs.length < 2) return;

      let scale: number;
      let result: { clip: CanonicalAnimationClip; droppedKeys: number };
      try {
        scale = clampScaleFactor(factor);
        result = scaleKeyframes(clip, state.keySelection, factor);
      } catch (error) {
        reportError(ctx, isAppError(error) ? error.message : String(error));
        return;
      }
      if (result.clip === clip) return;

      let pivot = refs[0].frame;
      for (const ref of refs) if (ref.frame < pivot) pivot = ref.frame;
      const selection = dedupeSelection(
        refs.map((ref) => ({
          bone: ref.bone,
          frame: Math.max(0, pivot + Math.round((ref.frame - pivot) * scale)),
        })),
      );
      const live = reposeLiveForClipEdit(clip, result.clip, state, state.playhead.frame);
      if (
        !commitClipEdit(ctx, 'Scale keys', result.clip, {
          nextSelection: selection,
          nextLivePose: live.livePose,
          nextLiveHipsY: live.liveHipsY,
        })
      ) {
        return;
      }

      if (result.droppedKeys > 0) {
        showToast(
          ctx,
          'info',
          `${result.droppedKeys} keyframe${result.droppedKeys === 1 ? '' : 's'} dropped by the scale.`,
        );
      }
    },

    beginKeyDrag(): void {
      dragBaseline = ctx.getState().clip;
      dragging = true;
      ctx.history.beginCoalesce('Move keyframes', KEY_DRAG_COALESCE_KEY);
    },

    endKeyDrag(): void {
      const baseline = dragBaseline;
      dragBaseline = null;
      dragging = false;
      ctx.history.endCoalesce();

      // Net loss over the WHOLE gesture. Dragging off the front of the sheet and back may cost
      // nothing (nothing collided) or may cost a key that is gone for good; the per-step counts
      // cannot tell those apart, the clip can.
      if (!baseline) return;
      const dropped = keyCount(baseline) - keyCount(ctx.getState().clip);
      if (dropped > 0) reportDroppedKeys(ctx, dropped);
    },

    setEasingForSelection(preset): void {
      const state = ctx.getState();
      if (!state.clip || state.keySelection.length === 0) return;
      const next = setEasing(state.clip, state.keySelection, preset);
      commitClipEdit(ctx, `Set easing ${preset}`, next);
    },

    copyKeys(): void {
      const state = ctx.getState();
      if (!state.clip || state.keySelection.length === 0) return;
      const payload = copyKeyframes(state.clip, state.keySelection);
      if (payload.keyframes.length === 0) return;
      ctx.set({ clipboard: payload });
    },

    pasteKeys(atFrame): void {
      const state = ctx.getState();
      const payload = state.clipboard;
      if (!state.clip || !payload || payload.keyframes.length === 0) return;

      const base = Math.max(0, Math.round(atFrame ?? state.playhead.frame));
      const next = pasteKeyframes(state.clip, payload, base);
      const selection = dedupeSelection(
        payload.keyframes
          .filter((k) => isCanonicalBoneId(k.bone))
          .map((k) => ({ bone: k.bone, frame: Math.max(0, base + Math.round(k.frameOffset)) })),
      );
      commitClipEdit(ctx, 'Paste keyframes', next, selection);
    },

    insertKeyAt(bone, frame, rotation, options): void {
      const state = ctx.getState();
      if (!state.clip) return;
      const target = Math.max(0, Math.round(frame));
      const next = setKeyframe(state.clip, bone, target, rotation, { hipsY: options?.hipsY });
      commitClipEdit(ctx, `Insert key ${bone}`, next);
    },

    deleteKey(bone, frame): void {
      const state = ctx.getState();
      if (!state.clip) return;
      const target = Math.max(0, Math.round(frame));
      const next = deleteKeyframes(state.clip, [{ bone, frame: target }]);
      commitClipEdit(ctx, `Delete key ${bone}`, next);
    },
  };
}
